import React, { createContext, useRef, useEffect, useState } from 'react';
import { useTranslation } from "./TranslationContext";
import { ConnectionEventEmitter } from '../utils/ConnectionEventEmitter';
import config from '../config'; 
import log from "loglevel";

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const ws = useRef(null);
  const { t } = useTranslation();
  const reconnectDelayRef = useRef(config.websocket.reconnectDelayStart);
  const reconnectTimeout = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showConnecting, setShowConnecting] = useState(true);

  const connectWebSocket = () => {
    log.info(`Attempting to connect to ${config.websocket.url} with delay: ${reconnectDelayRef.current}ms`);
 
    if (ws.current && ws.current.readyState !== WebSocket.CLOSED) {
      log.info('WebSocket is already connected or connecting.');
      return;
    }
    ws.current = new WebSocket(config.websocket.url);
    
    ws.current.onopen = () => {
      log.info('WebSocket connected.');
      setIsConnected(true);
      ConnectionEventEmitter.emit('opened');
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
        reconnectTimeout.current = null;
      }
      reconnectDelayRef.current = config.websocket.reconnectDelayStart;
    };

    ws.current.onmessage = (event) => {
      try {
          const message = JSON.parse(event.data);
          ConnectionEventEmitter.emit('received', message);
      } catch (err) {
        log.error('Error parsing message:', event.data, err);
      }
    };

    ws.current.onclose = () => {
      log.info(`WebSocket disconnected. Reconnecting in ${reconnectDelayRef.current / 1000} seconds...`);
      setIsConnected(false);
      setShowConnecting(true);
      ConnectionEventEmitter.emit('closed');

      if (!reconnectTimeout.current) {
        log.info('Set timeout.');
        reconnectTimeout.current = setTimeout(() => {
          reconnectDelayRef.current = Math.min(
            reconnectDelayRef.current + config.websocket.reconnectStep,
            config.websocket.reconnectDelayEnd
          );
          connectWebSocket();
        }, reconnectDelayRef.current);
      }
   };

    ws.current.onerror = (error) => {
      ConnectionEventEmitter.emit('closed');

      log.info('WebSocket error:', error);
      ws.current.close();
      reconnectTimeout.current = null;
    };
  };

  useEffect(() => {
    log.info('WebSocket useEffect');

    connectWebSocket();

    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current);
      }
    };
  }, []);

  const sendMessage = (message) => {
    log.debug("Send message: "+JSON.stringify(message));

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(message));
    } else {
      log.info('WebSocket is not open. Message not sent:', message);
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, isConnected, setShowConnecting }}>
      <>
        {children}
        {showConnecting && (
          <div className="connecting">
            <div className="connecting-loading">
              <div className="connecting-loading-dots">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            {t.general.Connecting}
          </div>
        )}
      </>
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => React.useContext(WebSocketContext);